import { ArrowLeft, ArrowRight, Plus, RotateCcw, Trash2, FileCheck2, Crop, Loader2, CheckCircle2, Camera, Images, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Tool = ({ onClick, disabled, icon: Icon, label, tone = "text-slate-700", testId }) => (
  <button type="button" onClick={onClick} disabled={disabled} className={`py-2.5 flex flex-col items-center gap-0.5 text-[10px] leading-tight ${tone} disabled:opacity-30 active:bg-slate-100`} aria-label={label} data-testid={testId}>
    <Icon className="w-4 h-4" />{label}
  </button>
);

const PageCard = ({ p, i, total, saving, on }) => (
  <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid={`scan-page-${i}`}>
    <div className="relative aspect-[3/4] bg-slate-100">
      {p.scan ? <img src={p.mode === "color" ? p.color : p.scan} alt={`Page ${i + 1}`} className="absolute inset-0 w-full h-full object-contain" />
        : <div className="absolute inset-0 grid place-items-center text-xs text-slate-500" data-testid={`scan-page-${i}-processing`}><span className="flex flex-col items-center gap-2"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />Cleaning up…</span></div>}
      <span className="absolute top-2 left-2 text-xs font-semibold bg-black/70 text-white rounded-full px-2 py-0.5">{i + 1}</span>
      {p.scan && !p.detected && !p.adjusted && (
        <button onClick={() => on.adjust(i)} className="absolute bottom-2 left-2 right-2 text-[11px] text-center bg-amber-100 text-amber-900 rounded px-1 py-1 font-medium" data-testid={`scan-page-${i}-edges-warning`}>Edges not found — tap to set corners</button>
      )}
    </div>
    <div className="grid grid-cols-2 text-xs border-t border-slate-200">
      {[["scan", "Clean scan"], ["color", "Color"]].map(([m, label]) => (
        <button key={m} onClick={() => on.mode(i, m)} className={`py-2 font-medium transition-colors ${p.mode === m ? "bg-[#002FA7] text-white" : "text-slate-600"}`} data-testid={`scan-page-${i}-${m}`}>{label}</button>
      ))}
    </div>
    <div className="grid grid-cols-5 border-t border-slate-200 divide-x divide-slate-100">
      <Tool onClick={() => on.move(i, -1)} disabled={saving || i === 0} icon={ArrowLeft} label="Left" testId={`scan-page-${i}-left`} />
      <Tool onClick={() => on.adjust(i)} disabled={saving || !p.scan} icon={Crop} label="Crop" testId={`scan-page-${i}-adjust`} />
      <Tool onClick={() => on.retake(i)} disabled={saving} icon={RotateCcw} label="Retake" testId={`scan-page-${i}-retake`} />
      <Tool onClick={() => on.remove(i)} disabled={saving} icon={Trash2} label="Delete" tone="text-rose-600" testId={`scan-page-${i}-remove`} />
      <Tool onClick={() => on.move(i, 1)} disabled={saving || i === total - 1} icon={ArrowRight} label="Right" testId={`scan-page-${i}-right`} />
    </div>
  </div>
);

export const ScanReview = ({ pages, saving, done, reading, on, onAddPage, onGallery, onSave, onBack }) => {
  const processing = pages.some((p) => !p.scan);
  if (done) {
    return (
      <div className="fixed inset-0 z-50 bg-white grid place-items-center p-8 text-center" data-testid="scanner-upload-success">
        <div className="space-y-3">
          <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-500" />
          <p className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Uploaded</p>
          <p className="text-sm text-slate-500">{pages.length} page{pages.length === 1 ? "" : "s"} saved as one PDF.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 bg-slate-50 flex flex-col" data-testid="scanner-review">
      <div className="h-14 shrink-0 bg-white border-b border-slate-200 px-3 flex items-center justify-between">
        <button onClick={onBack} disabled={saving} className="flex items-center gap-1.5 text-sm text-slate-600 p-2" data-testid="scanner-review-back-btn"><X className="w-4 h-4" /> Cancel</button>
        <div className="font-semibold" style={{ fontFamily: "Outfit" }} data-testid="scanner-review-title">{pages.length ? `${pages.length} page${pages.length === 1 ? "" : "s"}` : "Scan document"}</div>
        <div className="w-20" />
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {reading && <div className="max-w-xl mx-auto mb-3 flex items-center gap-2 text-sm text-[#002FA7]" data-testid="scanner-reading"><Loader2 className="w-4 h-4 animate-spin" /> Opening photo…</div>}
        {pages.length === 0 && !reading && (
          <div className="text-center py-14 space-y-4" data-testid="scanner-empty">
            <p className="text-sm text-slate-500">Take a photo of the paperwork with your camera.</p>
            <Button onClick={onAddPage} className="h-14 px-8 rounded-2xl bg-[#002FA7] hover:bg-[#001A75] text-base" data-testid="scanner-take-photo-btn"><Camera className="w-5 h-5 mr-2" /> Take photo</Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 max-w-xl mx-auto">
          {pages.map((p, i) => <PageCard key={p.id} p={p} i={i} total={pages.length} saving={saving} on={on} />)}
        </div>
      </div>
      <div className="shrink-0 bg-white border-t border-slate-200 p-4 pb-6 grid grid-cols-[1fr_auto_1fr] gap-2 max-w-xl w-full mx-auto">
        <Button variant="outline" onClick={onAddPage} disabled={saving} className="h-12 rounded-xl" data-testid="scanner-add-page-btn"><Plus className="w-4 h-4 mr-1" /> {pages.length ? "Add page" : "Camera"}</Button>
        <Button variant="outline" onClick={onGallery} disabled={saving} className="h-12 rounded-xl px-3" aria-label="Choose from photos" title="Choose from photos" data-testid="scanner-gallery-btn"><Images className="w-4 h-4" /></Button>
        <Button onClick={onSave} disabled={saving || processing || pages.length === 0} className="h-12 rounded-xl bg-[#002FA7] hover:bg-[#001A75]" data-testid="scanner-save-btn">
          {saving || processing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileCheck2 className="w-4 h-4 mr-1.5" />}
          {saving ? "Uploading…" : processing ? "Cleaning up…" : `Save PDF (${pages.length})`}
        </Button>
      </div>
    </div>
  );
};
